<template>
    <teleport to="body">
        <div v-if="showPopup" class="info-overlay" @keydown.esc="close">
            <div class="info-modal" role="dialog" :aria-label="title">
                <header class="info-header">
                    <h3>{{ title }}</h3>
                    <button class="info-close" @click="close">✕</button>
                </header>
                <section class="info-body">
                    <slot />
                    <div v-if="showImage && imageSrc" class="image-preview">
                        <img :src="imageSrc" :alt="imageAlt" />
                    </div>
                </section>
                <footer class="info-footer">
                    <button v-if="showCancel" class="info-exit info-cancel" @click="cancelAndClose">Cancel</button>
                    <button v-if="showConfirm" class="info-exit info-confirm" @click="confirmAndClose">Confirm</button>
                    <button v-if="showImage && imageSrc && showDownload" class="info-exit info-download"
                        @click="downloadImage">Download</button>
                </footer>
            </div>
        </div>
    </teleport>
</template>

<script>
import { defineComponent, computed } from 'vue';

export default defineComponent({
    name: 'InfoPopup',
    props: {
        showPopup: { type: Boolean, required: true },
        title: { type: String, default: 'Info' },
        imageBase64: { type: String, default: '' },
        showImage: { type: Boolean, default: false },
        showConfirm: { type: Boolean, default: true },
        showCancel: { type: Boolean, default: true },
        showDownload: { type: Boolean, default: true },
        downloadFilename: { type: String, default: 'image.png' },
        imageAlt: { type: String, default: 'Image preview' },
    },
    emits: ['update:showPopup', 'confirm', 'cancel'],
    setup(props, { emit }) {
        const imageSrc = computed(() => {
            if (!props.showImage || !props.imageBase64) return '';
            const b64 = props.imageBase64.trim();
            if (b64.startsWith('data:image')) return b64;
            return `data:image/png;base64,${b64}`;
        });

        function downloadImage() {
            if (!imageSrc.value) return;
            const link = document.createElement('a');
            link.href = imageSrc.value;
            link.download = props.downloadFilename || 'image.png';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }

        function close() {
            emit('update:showPopup', false);
        }
        function confirmAndClose() {
            emit('confirm');
            emit('update:showPopup', false);
        }
        function cancelAndClose() {
            emit('cancel');
            emit('update:showPopup', false);
        }
        return { close, confirmAndClose, cancelAndClose, imageSrc, downloadImage };
    }
});
</script>

<style scoped>
.info-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
}

.info-modal {
    background: #fff;
    width: 500px;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.info-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
}

.info-close {
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
}

.info-body {
    padding: 1rem;
    flex: 1;
    overflow-y: auto;
}

.info-footer {
    padding: 0.75rem 1rem;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
}

.info-exit {
    margin-left: 20px;
    padding: 6px 14px;
    background: var(--background-white-hover);
    border: 1px solid transparent;
    border-radius: 4px;
    cursor: pointer;
    transition: background .2s;
}

.info-exit:hover {
    background: var(--background-white-hover-darker);
}

.info-confirm {
    background-color: var(--purple);
    color: white;
    font-weight: 500;
}

.info-confirm:hover {
    background-color: var(--purple-hover);
    color: white;
}

.image-preview {
    margin-top: 0.5rem;
    display: flex;
    justify-content: center;
}

.image-preview img {
    max-width: 100%;
    height: auto;
    border: 1px solid var(--border-color);
    border-radius: 6px;
}

.info-download {
    background-color: var(--purple);
    color: white;
    font-weight: 500;
}

.info-download:hover {
    background-color: var(--purple-hover);
    color: white;
}
</style>
